import { useEffect, useMemo } from "react";

import { useGetCreditsQuery } from "@/pages/Private/redux/usage/usage.api";

import { CreditBalanceState } from "./creditBreakdown";

export interface CreditBalance {
	balance: CreditBalanceState;
	/** Whether the company has a subscription credits can be bought on. */
	hasSubscription: boolean;
	refetch: () => void;
}

/**
 * The balance a credit-confirm dialog compares its total with: the app's own credit query, fetched
 * fresh when the dialog opens and again whenever the window regains focus, so a top-up finished in
 * the checkout tab shows here without losing the selection (PREC-4263). A request in flight is
 * "loading", even over a cached answer; a failed query is
 * "unavailable", never a made-up number. A form that stays mounted while closed passes `active`
 * false until it opens, so it neither fetches nor listens then.
 */
export function useCreditBalance(active = true): CreditBalance {
	const { data, isError, isFetching, refetch } = useGetCreditsQuery(undefined, {
		refetchOnMountOrArgChange: true,
		skip: !active,
	});

	useEffect(() => {
		if (!active) {
			return;
		}

		const onFocus = () => {
			void refetch();
		};

		window.addEventListener("focus", onFocus);

		return () => window.removeEventListener("focus", onFocus);
	}, [active, refetch]);

	// A balance cached by the sidebar is not the one this dialog asked for: while the fresh request
	// runs it is loading, so Confirm waits and no "balance after" is built from a stale number
	// (decision "loading means disabled"; review S3).
	const balance = useMemo<CreditBalanceState>(() => {
		if (isFetching) {
			return { status: "loading" };
		}

		if (isError) {
			return { status: "unavailable" };
		}

		return data ? { status: "loaded", credits: data.credits } : { status: "loading" };
	}, [data, isError, isFetching]);

	return {
		balance,
		hasSubscription: !!data?.stripeSubscription,
		refetch: () => void refetch(),
	};
}
